<template>
  <div class="welcome-wrapper" :class="{ stamp: isStamp, incein: isIncein }">
    <template v-if="isStamp">
      <component
        :is="hoverButton"
        :show-hint="bubbleHint"
        :breathing="breathing"
        class="mx-auto mt-40 lg:mt-56"
        @click="next"
      />
      <stamp-welcome-to-scan v-if="isStamp" :error="errorMsg" :mute="step > 1" />
    </template>
    <template v-else>
      <welcome-to-scan :step="step" :title="$t('welcome.welcome_title')" />
      <panel :height="panelHeight">
        <component
          :is="hoverButton"
          :show-hint="bubbleHint"
          :breathing="breathing"
          class="mx-auto mt-5 md:mt-20"
          @click="next"
        />
        <p class="powered">{{ $t('welcome.powered_by') }}</p>
      </panel>
    </template>
    <qr-scan
      v-show="showScan"
      :steps="3"
      :step="loginProgress"
      :message="hint"
      @done="done"
      @cancel="reset"
    />
    <setting-user-data
      v-if="canScanAndCreate && showProfileSetting"
      class="user-setting"
      embedded
      :profile="userProfile"
      @save="onUserCreated"
    />
    <!-- Invisible area for toggling app menu -->
    <div class="fixed bottom-0 right-0 w-16 h-16" @click="showMenu"></div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import op from 'object-path';
import stampBgm from '@/mixins/stamp-bgm';
import page from '@/mixins/page';
import audioPlayer from '@/mixins/audio-player';
import scannerError from '@/mixins/scanner-error';

import { EVENTS as HW_EVENTS } from '@/events/hardware';
import ApiActions from '@/modules/api-actions';
import Storage from '@/modules/storage';
import { wait } from '@/modules/utils';
import ScanProcess from '@/modules/scan-process';
import i18n from '@/i18n';

import QrScan from '@/components/Welcome/QrScan.vue';
import HoverButton from '@/components/Welcome/HoverButton.vue';
import StampHoverButton from '@/components/Stamp/HoverButton.vue';
import StampWelcomeToScan from '@/components/Stamp/WelcomeToScan.vue';
import WelcomeToScan from '@/components/Welcome/WelcomeToScan.vue';
import Panel from '@/components/common/Panel.vue';
import SettingUserData from '@/views/SettingUserData.vue';

const STEPS = {
  IDLE: 1,
  WAKE: 2,
  CAMERA_HINT: 3,
  LAUNCH_SCAN: 4,
};

const SCANNER_STATES = {
  NOTHING: 0,
  WAITING: 1,
  INITIALIZING: 2,
};

const USER_CREATED_EVENT = 'user-created';

export default {
  name: 'Welcome',
  mixins: [page, stampBgm, audioPlayer, scannerError],
  components: {
    QrScan,
    HoverButton,
    StampHoverButton,
    WelcomeToScan,
    StampWelcomeToScan,
    Panel,
    SettingUserData,
  },
  props: {
    autoScan: Boolean,
    error: String,
  },
  beforeDestroy() {
    this.$events.off(HW_EVENTS.QRCODE_SUCCESS, this.handleQRCodeReadSuccess);
    clearTimeout(this.cameraTimer);
  },
  async mounted() {
    this.tryAutoCancel();
    this.$events.on(HW_EVENTS.QRCODE_SUCCESS, this.handleQRCodeReadSuccess);
    this.cameraTimer = null;
    if (this.error) {
      this.reset(this.error);
    }
    if (this.autoScan) {
      this.step = STEPS.LAUNCH_SCAN - 1;
      this.next();
    } else if (!this.isStamp) {
      this.playAudio('1-Welcome_and_tap', true);
    }
  },
  data: () => ({
    waitingForScanner: SCANNER_STATES.NOTHING,
    qrCodeResult: '',
    errorMsg: '',
    sessionKey: '',
    step: STEPS.IDLE,
    loginProgress: 0,
    hint: '',
    showScan: false,
    userProfile: {},
    showProfileSetting: false,
  }),
  methods: {
    ...mapActions('app', ['showMenu']),
    ...mapActions('user', {
      resetUser: 'reset',
    }),
    tryAutoCancel() {
      if (!ScanProcess.isOngoing()) return;

      const userToken = Storage.getUserToken();
      if (userToken) {
        console.log('trying to auto-cancel previous session');
        ApiActions.user.setToken(userToken);
        this.$nextTick(() => {
          ScanProcess.cancel().then(() => ApiActions.user.logout());
        });
      }
    },
    done() {
      console.log('Scanner connected. Proceeding...');
      if (this.isStamp) {
        this.$events.goto('StampThingsToNote');
      } else {
        this.$events.goto('ThingsToNote');
      }
    },
    next() {
      this.step += 1;
      if (this.step === STEPS.WAKE) {
        this.resetUser();
        this.startScanCode();
      }
    },
    reset(errorMessage, force) {
      this.hideCamera();
      if (errorMessage) {
        console.log(errorMessage);
        if (this.isStamp) {
          this.playNegative();
        }
        this.errorMsg = errorMessage;
        this.$events.alert(errorMessage);
        if (!force) {
          setTimeout(() => {
            this.errorMsg = '';
          }, 8000);
        }
      } else {
        this.errorMsg = '';
      }
      this.waitingForScanner = SCANNER_STATES.NOTHING;
      this.qrCodeResult = '';
      this.showScan = false;
      this.loginProgress = 0;
      this.step = STEPS.IDLE;
      this.userProfile = {};
      this.showProfileSetting = false;
    },
    startScanCode() {
      if (!this.isStamp) {
        this.playAudio('2-QRCode', true);
      }
      this.waitingForScanner = SCANNER_STATES.WAITING;
      this.hint = this.isStamp ? i18n.t('stamp.scan_code_hint') : i18n.t('welcome.scan_code_title');
      this.showScan = true;
      this.showCamera();
    },
    showCamera() {
      this.$events.emit(HW_EVENTS.START_SCAN_CODE);
      this.cameraTimer = setTimeout(() => {
        console.log('camera timer reset');
        this.reset();
      }, 40000);
    },
    hideCamera() {
      this.$events.emit(HW_EVENTS.STOP_SCAN_CODE);
      clearTimeout(this.cameraTimer);
      this.cameraTimer = null;
    },
    async findScanner() {
      this.loginProgress = 2;
      this.hint = i18n.t('welcome.scan_code_connect_to_scanner');
      try {
        let key = this.fixedSessionKey;
        if (!key) {
          key = await ScanProcess.getSessionKey(this.fixedScannerId);
        }
        await this.initializeScan(key);
      } catch (e) {
        this.reset(i18n.t('welcome.no_scanner_found'));
      }
    },
    async initializeScan(key) {
      if (this.waitingForScanner === SCANNER_STATES.INITIALIZING) return;

      this.sessionKey = key;
      this.waitingForScanner = SCANNER_STATES.INITIALIZING;
      try {
        console.log('initializing scanner', this.fixedScannerId, key);
        if (ScanProcess.isOngoing()) {
          await ScanProcess.cancel();
        }
        await ScanProcess.initial({ id: this.fixedScannerId, key });
        if (!this.isStamp) {
          this.playAudio('4-Ready_to_go', true);
        }
        this.loginProgress = 3;
        this.hint = i18n.t('welcome.scan_code_done');
      } catch (e) {
        const { errno, msg } = op.get(e, 'response.data.error', {});
        console.log(`[ERROR] Welcome initializeScan error: ${errno}, ${msg}`);
        const errorMessage = `${this.getScannerErrorHint(errno)}<br>(${errno})`;
        this.reset(errorMessage);
      }
    },
    async handleQRCodeReadSuccess(result) {
      console.log('handleQRCodeReadSuccess', result);
      if (this.qrCodeResult) return;

      this.qrCodeResult = result;
      if (this.isStamp) {
        this.playPositive();
      }
      this.hideCamera();

      if (result.includes('token=')) {
        const token = new URLSearchParams(result.split('?')[1]).get('token');
        if (token) {
          await this.loginUser(null, token);
          return;
        }
      }
      await this.loginUser(result);
    },
    onUserCreated() {
      this.$emit(USER_CREATED_EVENT);
    },
    async createUser() {
      this.showProfileSetting = true;
      this.$off(USER_CREATED_EVENT);
      await new Promise(resolve => {
        this.$once(USER_CREATED_EVENT, resolve);
      });
      this.showProfileSetting = false;
    },
    async loginUser(memberId, token=null) {
      this.errorMsg = '';
      this.hint = i18n.t('welcome.scan_code_logging_in');
      this.loginProgress = 1;
      this.stopAudio();
      if (!this.isStamp) {
        this.playAudio('3-Wait_a_moment', true);
      }
      try {
        if (token) {
          const expiredIn = 3600;  // 1 hour for token login
          ApiActions.user.setToken(token, expiredIn);
          Storage.setUserToken({ token, expiredIn });
        } else {
          const userAuth = await ApiActions.mtm.registerMember({
            member_number: memberId,
            provider: 0,
          });
          ApiActions.user.setAuth(userAuth);
          await ApiActions.user.auth(userAuth);
        }
        await wait(0.5);
        const profile = await ApiActions.user.getProfile();
        console.log('user login complete, gender: ', profile.gender);
        if (this.canScanAndCreate && !profile.gender) {
          // Show user setting screen if it's enabled
          this.userProfile = { ...profile };
          if (!profile.nick_name) {
            this.userProfile.nick_name = memberId;
          }
          await this.createUser();
        }
        if (this.fixedScannerId) {
          await this.findScanner();
        } else {
          this.reset(i18n.t('welcome.no_scanner_id'));
        }
      } catch (e) {
        const { response } = e;
        const { status } = response || {};
        let errorMessage = e.message;
        if (status === 400 || status === 401) {
          errorMessage = i18n.t('welcome.scan_code_no_account');
        }
        this.reset(errorMessage);
      }
    },
  },
  watch: {
    isBTReady: {
      handler(nv, ov) {
        if (this.waitingForScanner === SCANNER_STATES.WAITING) return;

        if (!nv) {
          this.reset(i18n.t('welcome.enable_bt'), true);
        } else if (nv && !ov) {
          this.reset();
        }
      },
      immediate: true,
    },
    step(nv) {
      if (this.isStamp) return;

      if (nv === STEPS.IDLE) {
        this.playAudio('1-Welcome_and_tap', true);
      }
    },
  },
  computed: {
    ...mapState('user', ['me']),
    ...mapState('mtmUser', ['fixedScannerId', 'fixedSessionKey']),
    ...mapState('hardware', {
      isBTReady: state => state.bluetooth.available,
    }),
    ...mapGetters('mtmUser', ['canScanAndCreate']),
    hoverButton() {
      return this.isStamp ? 'stamp-hover-button' : 'hover-button';
    },
    panelHeight() {
      return '65%';
    },
    bubbleHint() {
      return this.isStamp ? i18n.t('stamp.tap_to_start') : i18n.t('welcome.tap_camera_on');
    },
    breathing() {
      const { IDLE, CAMERA_HINT } = STEPS;
      return this.step === IDLE || this.step === CAMERA_HINT;
    },
  },
};
</script>

<style scoped lang="scss">
.welcome-wrapper {
  @apply flex flex-col;
  background-color: #202020;
  flex: 1;
  &.stamp {
    background-color: rgb(14, 14, 14);
  }
  &.incein {
    background-color: #fafaf9;
  }
  .powered {
    @apply absolute bottom-0 mb-1 left-0 right-0 mx-auto text-center;
    color: #767676;
    font-size: 14px;
    font-weight: 500;
    line-height: 3.21;
  }
  .error {
    @apply text-2xl text-center mt-4;
    @screen md {
      @apply mt-10;
    }
    font-size: 20px;
    color: red;
  }
  .user-setting {
    @apply absolute bg-white;
  }
}
</style>
